import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import { Input } from '@components/Input';
import { Switch } from '@components/Switch';
import { ModalBase } from '@components/Modal';
import TextEditor from '@components/TextEditor';
import PostDynamicVariables from '@features/PostDynamicVariables';
import { inboxTemplatesService } from '@services';
import { parseTipTapJSONToPlainText, testEditorLength } from '@helpers/text-editor';
import { alertActions, inboxTemplatesActions } from '@actions';
import { useTranslation } from '@hooks';
import usePermissions from '@hooks/usePermissions';
import { INBOX_PLACEHOLDER_TOKENS, PLACEHOLDER_CONTEXT } from '@constants/placeholders';

export const TEMPLATE_MODAL_MODE = {
    CREATE: 'CREATE',
    EDIT: 'EDIT',
};

const Modal = styled(ModalBase)({
    '& .modal-base-paper': {
        maxWidth: 640,
        width: 'calc(100% - 32px)',
    },
    '& .modal-base-content': {
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },
});

const MESSAGE_MAX_LENGTH = 1000;

const ManageTemplateModal = ({
    user,
    content,
    mode = TEMPLATE_MODAL_MODE.CREATE,
    selectedTemplate,
    onConfirmEdit,
    setAnchorElEdit,
    isOpen,
    setOpen,
}) => {
    const { t } = useTranslation();
    const { hasFullAccess } = usePermissions();
    const canManageTemplates = hasFullAccess('messaging');
    const dispatch = useDispatch();

    const isEdit = mode === TEMPLATE_MODAL_MODE.EDIT;

    const initialMessage = useMemo(() => parseTipTapJSONToPlainText(content), [content]);

    const [name, setName] = useState(isEdit ? (selectedTemplate?.name ?? '') : '');
    const [shared, setShared] = useState(isEdit ? !!selectedTemplate?.shared : canManageTemplates);
    const [message, setMessage] = useState(content);

    const messagePlainText = useMemo(() => parseTipTapJSONToPlainText(message), [message]);
    const isMessageWithinLimit = useMemo(
        () => testEditorLength(messagePlainText, MESSAGE_MAX_LENGTH, INBOX_PLACEHOLDER_TOKENS),
        [messagePlainText],
    );

    const close = () => {
        setOpen(false);
        setAnchorElEdit?.(null);
    };

    const onCreateTemplate = async () => {
        try {
            await inboxTemplatesService.addOrUpdateInboxTemplate({
                name,
                authorName: `${user.firstName} ${user.lastName}`,
                message: messagePlainText,
                shared,
            });
            dispatch(alertActions.success(t('alertMessages.createSuccess')));
            await dispatch(inboxTemplatesActions.getAll());
        } catch (error) {
            dispatch(alertActions.error(t('alertMessages.createFail')));
        }
        close();
    };

    const onSave = () => {
        if (isEdit) {
            onConfirmEdit?.({ name, shared, message: messagePlainText });
            setOpen(false);
            return;
        }
        onCreateTemplate();
    };

    const title = isEdit ? t('RespondTemplate.overwriteTemplate') : t('RespondTemplate.saveNewTemplate');

    return (
        <Modal
            isOpen={isOpen}
            handleClose={() => setOpen(false)}
            title={title}
            onPrimaryAction={onSave}
            primaryActionText={t('buttons.save')}
            primaryActionDisabled={!name || !messagePlainText || !isMessageWithinLimit}
        >
            <Input
                inputProps={{ name: 'name', value: name, onChange: e => setName(e.target.value) }}
                fullWidth
                label={t('common.templateName')}
                required
            />
            <TextEditor
                label={t('common.message', { count: 1 })}
                initialValue={initialMessage}
                onChange={setMessage}
                limit={MESSAGE_MAX_LENGTH}
                placeholderContext={PLACEHOLDER_CONTEXT.INBOX}
                error={!isMessageWithinLimit}
                toolbar={<PostDynamicVariables />}
            />
            <Switch
                checked={shared}
                handleChange={event => setShared(event.target.checked)}
                label={t('common.sharedTemplate')}
                disabled={!canManageTemplates}
            />
        </Modal>
    );
};

export default ManageTemplateModal;
